import { Injectable, signal } from '@angular/core';
import { ECarSettings } from '../../models/ecar';

/**
 * Widget (embeddable) mode — the app served at the root of the widget host
 * (`widget.<domain>`, see isWidgetHost). No other host or path runs the widget; the old
 * `/widget` path redirects to the root (app.routes.ts).
 *
 * In this mode the app skips everything that is not the map itself: no welcome /
 * first-car dialogs, no cookie banner, no profile menu, no account login. Every car
 * parameter and every setting can be given through URL query parameters; the values
 * are applied on top of whatever localStorage holds, so the URL always wins at load.
 *
 * The service reads `window.location` directly (not ActivatedRoute) because it has to
 * be usable before the router resolves anything — SettingsService and the init resolver
 * run ahead of the first NavigationEnd.
 *
 * Query parameters (names are case-insensitive):
 *   api_key=<key>     REQUIRED. Without a valid key the widget is not loaded at all:
 *                     widgetApiKeyGuard renders the WidgetErrorComponent instead.
 *
 * The rest are optional:
 *   carselector=0|1   car selector (vehicle options dialog) reachable      default 1
 *   batteryslider=0|1 battery level slider shown in the vehicle panel        default 1
 *   settings=0|1      settings icon shown in the top-right corner            default 1
 *   stationlists=0|1  favorite / disabled charging station lists           default 1
 *                     (0: no UI for them, stored lists ignored)
 *   operatorlists=0|1 preferred / avoided charging operator lists          default 1
 *                     (0: no UI for them, stored lists ignored)
 *
 * GPS: the widget never asks the browser for geolocation. The embedding page pushes
 * positions through the JS API registered by WidgetApiService:
 *   window.evWidget.gpsPositionUpdate(lat, lng, speed?, heading?, accuracy?, altitude?, timestamp?)
 *   window.gpsPositionUpdate(...)          (alias)
 *   window.postMessage({ type: 'gpsPositionUpdate', lat, lng, ... }, '*')   (iframe embedding)
 *   window.evWidget.setBattery(level) / postMessage({ type: 'setBattery', level })
 *
 *   carid=<id>        vehicle id (ECarType.Id)
 *   battery=1..100    state of charge %
 *   weight=1..5       passengers
 *   tirepressure=1.5..2.6   bar
 *   userbehavior=0..100     0 efficiency focused … 100 performance oriented
 *   speedlimiter=80..130    km/h
 *   minbattery=10..75       min. battery at destination %
 *   reserverange=20..50     battery reserve in range mode % (batterySafetyLimitRha)
 *   reserveroute=10..30     battery reserve in route mode % (batterySafetyLimitRoute)
 *   soh=50..110             battery state of health %
 *   fastchargelimit=75..90  %
 *   chargers=ccs,chademo,schuko,tesla,type1,type2   charger types (comma list)
 *   favoriteoperators=<id>,<id>   preferred charging operators (ChargingOperator.id list);
 *                                 replaces the stored list, applies even with operatorlists=0
 *
 *   lang=bg|br|ca|cs|cy|da|de|el|en|es|et|eu|fi|fr|ga|gl|hi|hr|hu|id|is|it|ja|lb|lt|lv|mk|mt|nl|no|pl|pt|ro|ru|se|sk|sl|sq|sr|tr|uk|vi|zh
 *   skin=light|dark
 *   color_<name>=<color>          overrides one colors.scss token, see WIDGET_COLOR_PARAMS
 *   maptint=0..100                how far the map follows the palette; default 25 when any
 *                                 color_* is given, otherwise 0 (see MapPaletteService)
 *   pois=0|1
 *   unit=metric|imperial          sets all four units at once
 *   distance|temperature|speed|pressure=metric|imperial
 *   tollroads|ferries|highways=0|1
 *   mute=0|1                      voice guidance muted
 *
 *   mode=range|route
 *   lat=<deg>&lon=<deg>           start position
 *   zoom=<n>                      map zoom used together with lat/lon
 */
/** Former widget path; now only a redirect to the root (app.routes.ts). */
export const WIDGET_PATH = 'widget';

/** First DNS label of the host that serves the widget at its root (`widget.evnavigation.com`). */
export const WIDGET_HOST_LABEL = 'widget';

/**
 * True on the widget host: there the root path is the widget, and the full-site routes are
 * not reachable (app.routes.ts). The same build serves both hosts; only the hostname differs.
 */
export function isWidgetHost(): boolean {
  try {
    return window.location.hostname.toLowerCase().split('.')[0] === WIDGET_HOST_LABEL;
  } catch {
    return false;
  }
}

const CHARGER_TYPE_INDEX: Record<string, number> = {
  ccs: 0, chademo: 1, schuko: 2, tesla: 3, supercharger: 3, type1: 4, type2: 5,
};

/** A `color_*` query parameter and the colors.scss tokens it overrides. */
export interface WidgetColorParam {
  name: string;
  tokens: string[];
  description: string;
}

/**
 * Colors the embedding page can override. The value replaces the token in both skins; the
 * gradients, hover states and tints built from these tokens in colors.scss follow along.
 */
export const WIDGET_COLOR_PARAMS: WidgetColorParam[] = [
  { name: 'color_accent1', tokens: ['--base-gradient-color-1'], description: 'Start of the accent gradient: primary buttons, switches, sliders, highlights.' },
  { name: 'color_accent2', tokens: ['--base-gradient-color-2'], description: 'End of the accent gradient; route plan headings in the light skin.' },
  { name: 'color_background', tokens: ['--base-background-color'], description: 'Background of panels, cards and dialogs.' },
  { name: 'color_darkbackground', tokens: ['--base-dark-background-color'], description: 'Dark surfaces kept dark in both skins: select panels, switch and slider tracks, navigation turn card.' },
  { name: 'color_text', tokens: ['--base-font-color'], description: 'Main text and icon color on the panel background.' },
  { name: 'color_lighttext', tokens: ['--base-white-font-color', '--base-white-color'], description: 'Text and icons on the accent gradient and on dark surfaces; light pills and borders.' },
  { name: 'color_darktext', tokens: ['--base-dark-font-color'], description: 'Text on light pills and light buttons.' },
  { name: 'color_warning', tokens: ['--warning-color'], description: 'Warnings, errors and destructive actions.' },
];

/**
 * Normalises a color given in the URL. `#` has to be URL-encoded (`%23`), so bare hex digits
 * (`2FA9D2`, `fff`) are accepted too; anything else must be a valid CSS color (`red`,
 * `rgb(47 169 210)`). Returns null for values the browser would not accept as a color.
 */
export function normalizeWidgetColor(raw: string | null | undefined): string | null {
  const value = raw?.trim();
  if (!value || /[;{}<>]/.test(value)) return null;
  const color = /^([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value) ? '#' + value : value;
  try {
    if (typeof CSS !== 'undefined' && CSS.supports) {
      return CSS.supports('color', color) ? color : null;
    }
  } catch { }
  return /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(color) ? color : null;
}

export interface WidgetCarParams {
  carId: number | null;
  settings: Partial<ECarSettings>;
  chargerTypes: boolean[] | null;
}

@Injectable({ providedIn: 'root' })
export class WidgetService {

  private readonly params = new Map<string, string>();
  private readonly widget: boolean;

  /** True when the app was opened on the widget host (see isWidgetHost). */
  public readonly isWidget = signal<boolean>(false);

  constructor() {
    this.widget = isWidgetHost();
    this.isWidget.set(this.widget);
    if (this.widget) {
      this.parseParams();
    }
  }

  private parseParams(): void {
    try {
      new URLSearchParams(window.location.search).forEach((value, key) => {
        this.params.set(key.toLowerCase(), value.trim());
      });
    } catch { }
  }

  // ---------------------------------------------------------------------------
  // Raw accessors
  // ---------------------------------------------------------------------------

  public has(name: string): boolean {
    return this.widget && this.params.has(name.toLowerCase());
  }

  public getString(name: string): string | null {
    if (!this.widget) return null;
    const value = this.params.get(name.toLowerCase());
    return value != null && value.length > 0 ? value : null;
  }

  public getNumber(name: string, min?: number, max?: number): number | null {
    const raw = this.getString(name);
    if (raw == null) return null;
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return null;
    if (min != null && value < min) return min;
    if (max != null && value > max) return max;
    return value;
  }

  /** Accepts 1/0, true/false, yes/no, on/off. */
  public getBoolean(name: string): boolean | null {
    const raw = this.getString(name)?.toLowerCase();
    if (raw == null) return null;
    if (['1', 'true', 'yes', 'on'].includes(raw)) return true;
    if (['0', 'false', 'no', 'off'].includes(raw)) return false;
    return null;
  }

  // ---------------------------------------------------------------------------
  // Access
  // ---------------------------------------------------------------------------

  /** `api_key` — the key the embedding site was issued. Validated by WidgetAuthService. */
  public getApiKey(): string | null {
    return this.getString('api_key');
  }

  // ---------------------------------------------------------------------------
  // UI switches
  // ---------------------------------------------------------------------------

  /** `carselector=0` locks the vehicle: the options dialog cannot be opened. */
  public carSelectorEnabled(): boolean {
    return this.getBoolean('carselector') ?? true;
  }

  /** `settings=0` hides the settings icon. */
  public settingsEnabled(): boolean {
    return this.getBoolean('settings') ?? true;
  }

  /** `batteryslider=0` hides the battery level slider (battery comes from the URL / JS API). */
  public batterySliderEnabled(): boolean {
    return this.getBoolean('batteryslider') ?? true;
  }

  /**
   * `stationlists=0` hides the favorite / disabled charging station lists (charging options
   * page, status selector of the charger popup); stored lists are then not applied either.
   */
  public stationListsEnabled(): boolean {
    return this.getBoolean('stationlists') ?? true;
  }

  /**
   * `operatorlists=0` hides the preferred / avoided charging operator lists; stored lists are
   * then not applied either — only `favoriteoperators` from the URL is.
   */
  public operatorListsEnabled(): boolean {
    return this.getBoolean('operatorlists') ?? true;
  }

  /** Nothing left to show in the vehicle panel: no car selector and no battery slider. */
  public vehiclePanelEnabled(): boolean {
    return this.carSelectorEnabled() || this.batterySliderEnabled();
  }

  // ---------------------------------------------------------------------------
  // Car parameters
  // ---------------------------------------------------------------------------

  public getCarParams(): WidgetCarParams {
    const settings: Partial<ECarSettings> = {};
    const set = (key: keyof ECarSettings, value: number | null) => {
      if (value != null) settings[key] = value;
    };
    set('batteryLevel', this.getNumber('battery', 1, 100));
    set('weight', this.getNumber('weight', 1, 5));
    set('tirePressure', this.getNumber('tirepressure', 1.5, 2.6));
    set('userBehavior', this.getNumber('userbehavior', 0, 100));
    set('speedLimiter', this.getNumber('speedlimiter', 80, 130));
    set('minBatteryAtDestination', this.getNumber('minbattery', 10, 75));
    set('batterySafetyLimitRha', this.getNumber('reserverange', 20, 50));
    set('batterySafetyLimitRoute', this.getNumber('reserveroute', 10, 30));
    set('batteryStateOfHealth', this.getNumber('soh', 50, 110));
    set('fastChargeLimit', this.getNumber('fastchargelimit', 75, 90));

    return {
      carId: this.getNumber('carid'),
      settings,
      chargerTypes: this.parseChargerTypes(),
    };
  }

  private parseChargerTypes(): boolean[] | null {
    const raw = this.getString('chargers');
    if (raw == null) return null;
    const result = [false, false, false, false, false, false];
    let any = false;
    raw.toLowerCase().split(/[,;|+ ]+/).forEach((name) => {
      const idx = CHARGER_TYPE_INDEX[name.replace(/[^a-z0-9]/g, '')];
      if (idx != null) { result[idx] = true; any = true; }
    });
    return any ? result : null;
  }

  /**
   * `favoriteoperators=12,345` — preferred charging operator ids, separated like `chargers`.
   * Null when the parameter is missing or holds no valid id.
   */
  public getFavoriteOperatorIds(): number[] | null {
    const raw = this.getString('favoriteoperators');
    if (raw == null) return null;
    const ids = raw.split(/[,;|+ ]+/).filter((part) => /^\d+$/.test(part)).map(Number);
    return ids.length > 0 ? [...new Set(ids)] : null;
  }

  // ---------------------------------------------------------------------------
  // Settings
  // ---------------------------------------------------------------------------

  public getLanguage(): string | null {
    const lang = this.getString('lang')?.toLowerCase() ?? null;
    return lang && /^[a-z]{2}$/.test(lang) ? lang : null;
  }

  public getSkin(): 'light' | 'dark' | null {
    const skin = this.getString('skin')?.toLowerCase();
    return skin === 'light' || skin === 'dark' ? skin : null;
  }

  /** Token → color for every valid `color_*` parameter; invalid values are dropped. */
  public getColors(): { token: string, value: string }[] {
    const colors: { token: string, value: string }[] = [];
    WIDGET_COLOR_PARAMS.forEach((param) => {
      const value = normalizeWidgetColor(this.getString(param.name));
      if (value != null) {
        param.tokens.forEach((token) => colors.push({ token, value }));
      }
    });
    return colors;
  }

  /** True when the `color_*` parameter `name` holds a valid color. */
  public hasColor(name: string): boolean {
    return normalizeWidgetColor(this.getString(name)) != null;
  }

  /** `maptint=0..100` — how far the map follows the color palette (see MapPaletteService). */
  public getMapTint(): number | null {
    return this.getNumber('maptint', 0, 100);
  }

  /**
   * Sets the `color_*` overrides as inline custom properties on the body. Inline style beats
   * the `body` / `body.lightSkin` rules of colors.scss, so the override holds in both skins
   * and survives a skin switch inside the widget.
   */
  public applyColors(): void {
    this.getColors().forEach(({ token, value }) => document.body.style.setProperty(token, value));
  }

  public getPois(): boolean | null {
    return this.getBoolean('pois');
  }

  /** Per-unit override; falls back to the global `unit` parameter. */
  public getUnit(name: 'distance' | 'temperature' | 'speed' | 'pressure'): 'metric' | 'imperial' | null {
    const value = (this.getString(name) ?? this.getString('unit'))?.toLowerCase();
    return value === 'metric' || value === 'imperial' ? value : null;
  }

  public hasAnyUnit(): boolean {
    return ['unit', 'distance', 'temperature', 'speed', 'pressure'].some((name) => this.has(name));
  }

  public getRouting(name: 'tollroads' | 'ferries' | 'highways'): boolean | null {
    return this.getBoolean(name);
  }

  public getMute(): boolean | null {
    return this.getBoolean('mute');
  }

  // ---------------------------------------------------------------------------
  // Map / mode
  // ---------------------------------------------------------------------------

  public getMode(): 'range' | 'route' | null {
    const mode = this.getString('mode')?.toLowerCase();
    return mode === 'range' || mode === 'route' ? mode : null;
  }

  public getStartPosition(): { lat: number, lng: number } | null {
    const lat = this.getNumber('lat', -90, 90);
    const lng = this.getNumber('lon', -180, 180) ?? this.getNumber('lng', -180, 180);
    return lat != null && lng != null ? { lat, lng } : null;
  }

  public getZoom(): number | null {
    return this.getNumber('zoom', 1, 20);
  }
}
